/**
 * 겹치는 책으로 이어지는 우주.
 *
 * 예전 우주 뷰는 이웃 행성만 그렸다. 이웃이 되는 기준이
 * "겹치는 책 / 내 권수 ≥ 30%" 였는데, 이 값은 내 권수로 나누기 때문에
 * 많이 읽은 사람일수록 이웃이 생기지 않는다 — 50권을 담은 사람은
 * 15권이 겹쳐야 한 명이 이어진다.
 *
 * 게다가 기준을 낮춰도 그림은 채워지지 않는다. 실제 자료에서 한 권이라도
 * 겹치는 사람 쌍은 253쌍 중 19쌍뿐이라, 사람끼리 잇는 선은 19개가 천장이다.
 *
 * 그래서 잇는 방식을 바꾼다. 사람과 사람을 잇지 않고, 겹치는 책을 가운데
 * 노드로 두고 사람을 그 책에 잇는다. 같은 자료로 선이 49개가 된다.
 * 기준도 없앤다 — 한 권만 겹쳐도 이어진다.
 */

/** 두 명 이상이 담은 책만 남긴다. 혼자 담은 책은 아무도 잇지 못한다. */
export function buildGraph(users, myId) {
  const readers = new Map();       // bookId -> Set(userId)
  const byId = new Map();
  for (const u of users || []) {
    byId.set(u.id, u);
    for (const bid of u.bookIds || []) {
      if (!readers.has(bid)) readers.set(bid, new Set());
      readers.get(bid).add(u.id);
    }
  }

  const bookNodes = [];
  const edges = [];
  const touched = new Set();
  for (const [bid, set] of readers) {
    if (set.size < 2) continue;
    bookNodes.push({ id: `b${bid}`, kind: "book", bookId: bid, count: set.size });
    for (const uid of set) {
      edges.push({ a: `b${bid}`, b: `u${uid}` });
      touched.add(uid);
    }
  }

  // 아무 책도 겹치지 않는 사람은 그리지 않는다. 선 없는 점만 흩어져 있으면
  // 이어져 있다는 것이 이 화면의 요점인데 그 반대로 읽힌다.
  // 다만 나는 겹치는 책이 없어도 그린다 — 내가 없는 내 우주는 이상하다.
  const personNodes = [];
  for (const uid of touched) personNodes.push({ id: `u${uid}`, kind: "person", user: byId.get(uid) });
  if (myId && !touched.has(myId) && byId.has(myId)) {
    personNodes.push({ id: `u${myId}`, kind: "person", user: byId.get(myId), lonely: true });
  }

  return { nodes: [...bookNodes, ...personNodes], edges };
}

/**
 * 배치. Fruchterman–Reingold 를 짧게 돌린다.
 *
 * 시작 위치를 난수로 두면 화면을 다시 그릴 때마다 배치가 바뀌어,
 * 같은 우주가 매번 달라 보인다. 인덱스로 원 위에 놓아 늘 같게 만든다.
 */
export function layout(nodes, edges, { width = 1000, height = 700, iters = 320 } = {}) {
  if (nodes.length === 0) return [];

  /* 선이 하나도 없는 점은 힘 배치에서 뺀다.
     겹치는 책이 없는 사람(대개 나)은 아무것과도 당겨지지 않아 화면
     끝까지 밀려나고, 마지막에 좌표를 bbox 로 늘릴 때 그 점 하나가
     기준이 되어 나머지가 반대편에 뭉친다 — 실제로 화면 절반이 비었다.
     따로 세워두고, 이어진 것들만 배치한다. */
  const linked = new Set();
  for (const e of edges) { linked.add(e.a); linked.add(e.b); }
  const lonely = nodes.filter((nd) => !linked.has(nd.id));
  nodes = nodes.filter((nd) => linked.has(nd.id));

  const parked = lonely.map((nd, i) => ({
    ...nd,
    alone: true,
    /* 아래쪽에 나란히 세운다. 이어진 무리와 섞이지 않게 떨어뜨린다.
       0.94 로 두었더니 이름과 안내가 틀 밖으로 잘렸다 — 점 아래로
       55px 쯤 더 쓰이므로 그만큼 올려 잡는다. */
    px: (width / (lonely.length + 1)) * (i + 1),
    py: height * 0.86,
  }));

  const n = nodes.length;
  if (n === 0) return parked;
  const pos = nodes.map((nd, i) => {
    const a = (i / n) * Math.PI * 2;
    // 책을 안쪽, 사람을 바깥쪽에서 시작시키면 훨씬 빨리 자리를 잡는다
    const r = nd.kind === "book" ? 0.22 : 0.42;
    return { ...nd, x: 0.5 + Math.cos(a) * r, y: 0.5 + Math.sin(a) * r };
  });
  const idx = new Map(pos.map((p, i) => [p.id, i]));
  const k = Math.sqrt(1 / n);            // 이상적인 간격 (0~1 좌표계)
  let temp = 0.12;

  for (let it = 0; it < iters; it++) {
    const dx = new Float64Array(n), dy = new Float64Array(n);

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        let ex = pos[i].x - pos[j].x, ey = pos[i].y - pos[j].y;
        let d2 = ex * ex + ey * ey;
        if (d2 < 1e-9) { ex = (i % 7 + 1) * 1e-4; ey = (j % 5 + 1) * 1e-4; d2 = ex * ex + ey * ey; }
        const d = Math.sqrt(d2);
        const f = (k * k) / d;
        dx[i] += (ex / d) * f; dy[i] += (ey / d) * f;
        dx[j] -= (ex / d) * f; dy[j] -= (ey / d) * f;
      }
    }

    for (const e of edges) {
      const i = idx.get(e.a), j = idx.get(e.b);
      if (i === undefined || j === undefined) continue;
      let ex = pos[i].x - pos[j].x, ey = pos[i].y - pos[j].y;
      const d = Math.max(Math.sqrt(ex * ex + ey * ey), 1e-6);
      const f = (d * d) / k;
      dx[i] -= (ex / d) * f; dy[i] -= (ey / d) * f;
      dx[j] += (ex / d) * f; dy[j] += (ey / d) * f;
    }

    for (let i = 0; i < n; i++) {
      const d = Math.max(Math.hypot(dx[i], dy[i]), 1e-9);
      const step = Math.min(d, temp);
      pos[i].x += (dx[i] / d) * step;
      pos[i].y += (dy[i] / d) * step;
    }
    temp *= 0.97;
  }

  // 화면에 꽉 차게 늘린다. 여백은 라벨이 잘리지 않을 만큼만 둔다.
  const xs = pos.map(p => p.x), ys = pos.map(p => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const sx = (maxX - minX) || 1, sy = (maxY - minY) || 1;
  const PAD = 0.09;
  /* 따로 세운 점이 있으면 위쪽을 그만큼 비워 아래 자리와 겹치지 않게 한다 */
  const bottom = parked.length > 0 ? 0.84 : 1;
  return [
    ...pos.map(p => ({
      ...p,
      px: (PAD + ((p.x - minX) / sx) * (1 - PAD * 2)) * width,
      py: (PAD + ((p.y - minY) / sy) * (1 - PAD * 2)) * height * bottom,
    })),
    ...parked,
  ];
}
